// filepath: apps/web/src/editors/bpm-designer/queries.ts
//
// FR-UI-BLD-1 AC-3 / AC-4 - TanStack Query hooks for the BPM designer SPA.
//
// Cache keys follow the INV-1 pattern: ['admin', tenantSlug, ...].
// Mutations carry `credentials: 'include'` so the JWT cookie travels.
// The X-Idempotency-Key header is attached to every mutating verb
// (ARCH Rule 3 - idempotency at every entry point).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { apiFetch } from "@/api";
import type {
  FormSummary,
  ProcessCreateRequest,
  ProcessCreateResponse,
  RoleSummary,
  ScriptSummary,
} from "./types";

export const bpmDesignerKeys = {
  processSave: (tenantSlug: string) =>
    ["admin", tenantSlug, "process-save"] as const,
  formList: (tenantSlug: string) => ["admin", tenantSlug, "forms"] as const,
  roleList: (tenantSlug: string) => ["admin", tenantSlug, "roles"] as const,
  scriptList: (tenantSlug: string) => ["admin", tenantSlug, "scripts"] as const,
};

export function useProcessSave() {
  const { tenantSlug } = useTenant();
  const qc = useQueryClient();
  return useMutation<ProcessCreateResponse, Error, ProcessCreateRequest>({
    mutationFn: async (body) => {
      const r = await apiFetch("/api/process-definitions", {
        method: "POST",
        init: {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        idempotencyKey: true,
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        const err = new Error(
          `process save failed: ${r.status} ${JSON.stringify(errBody)}`,
        );
        (err as Error & { body?: unknown; status?: number }).body = errBody;
        (err as Error & { body?: unknown; status?: number }).status = r.status;
        throw err;
      }
      return (await r.json()) as ProcessCreateResponse;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["admin", tenantSlug, "processes"],
      });
    },
  });
}

export function useFormList() {
  const { tenantSlug } = useTenant();
  return useQuery<FormSummary[]>({
    queryKey: bpmDesignerKeys.formList(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch("/api/forms");
      if (!r.ok) {
        throw new Error(`failed to load forms: ${r.status}`);
      }
      return (await r.json()) as FormSummary[];
    },
  });
}

export function useRoleListForBpm() {
  const { tenantSlug } = useTenant();
  return useQuery<RoleSummary[]>({
    queryKey: bpmDesignerKeys.roleList(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch("/api/roles");
      if (!r.ok) {
        throw new Error(`failed to load roles: ${r.status}`);
      }
      return (await r.json()) as RoleSummary[];
    },
  });
}

export function useScriptList() {
  const { tenantSlug } = useTenant();
  return useQuery<ScriptSummary[]>({
    queryKey: bpmDesignerKeys.scriptList(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch("/api/scripts");
      if (!r.ok) {
        throw new Error(`failed to load scripts: ${r.status}`);
      }
      return (await r.json()) as ScriptSummary[];
    },
  });
}
